import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useHref } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { MasterFilters, MasterSummaryCards, MasterStatsRow, MasterToggle } from '@/pages/masters/common/commonComponents';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import {
    createOperator,
    getAllOperators,
    updateOperator,
    deleteOperator,
    getNextOperatorCode,
    type OperatorResponse,
    type OperatorSkill,
    type OperatorSkillLevel
} from '@/api/operators.api';
import { getAllShifts } from '@/api/shift.api';
import { userService } from '@/services/userService';
import { useWarehouseStore } from '@/stores/warehouseStore';

interface Operator {
    id: string;
    name: string;
    employeeCode: string;
    skill: OperatorSkill;
    wageRatePerHour: number;
    shiftId: string | null;
    shiftName?: string | null;
    phone: string | null;
    isActive: boolean;
    warehouseId?: string | null;
    skillLevel: OperatorSkillLevel;
    userId?: string | null;
    userName?: string | null;
}

interface ShiftOption {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    isActive: boolean;
    warehouseId?: string | null;
}

interface OperatorForm {
    name: string;
    employeeCode: string;
    skill: OperatorSkill;
    shiftId: string;
    wageRatePerHour: string;
    phone: string;
    isActive: boolean;
    skillLevel: OperatorSkillLevel;
    /** Login user linked to the operator ('' = none) */
    userId: string;
}

const SKILL_LEVEL_OPTIONS: { value: OperatorSkillLevel; label: string; hint: string; cls: string }[] = [
    { value: 'TRAINEE', label: 'Trainee', hint: 'Works under supervisor watch', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    { value: 'SKILLED', label: 'Skilled', hint: 'Runs regular jobs independently', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    { value: 'EXPERT', label: 'Expert', hint: 'Can be put on critical jobs', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const skillLevelBadge = (level: OperatorSkillLevel) =>
    SKILL_LEVEL_OPTIONS.find((o) => o.value === level) || SKILL_LEVEL_OPTIONS[1];

const PHONE_RE = /^\d{10}$/;


const BASE_SKILL_OPTIONS: { value: OperatorSkill; label: string }[] = [
    { value: 'WELDER', label: 'Welder' },
    { value: 'MACHINIST', label: 'Machinist' },
    { value: 'ASSEMBLER', label: 'Assembler' },
    { value: 'QC_INSPECTOR', label: 'QC Inspector' },
    { value: 'SUPERVISOR', label: 'Supervisor' },
];

const KNOWN_SKILL_BADGE: Record<string, { label: string; cls: string }> = {
    WELDER: { label: 'Welder', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
    MACHINIST: { label: 'Machinist', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    ASSEMBLER: { label: 'Assembler', cls: 'bg-green-50 text-green-700 border-green-200' },
    QC_INSPECTOR: { label: 'QC Inspector', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
    SUPERVISOR: { label: 'Supervisor', cls: 'bg-red-50 text-red-700 border-red-200' },
};

function formatSkillLabel(skill: string): string {
    if (!skill) return '';
    if (KNOWN_SKILL_BADGE[skill]) return KNOWN_SKILL_BADGE[skill].label;
    return skill.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

const BADGE_COLOR_PALETTES = [
    'bg-teal-50 text-teal-700 border-teal-200',
    'bg-indigo-50 text-indigo-700 border-indigo-200',
    'bg-pink-50 text-pink-700 border-pink-200',
    'bg-cyan-50 text-cyan-700 border-cyan-200',
    'bg-amber-50 text-amber-700 border-amber-200',
    'bg-emerald-50 text-emerald-700 border-emerald-200',
];

function getSkillBadge(skill: string): { label: string; cls: string } {
    if (KNOWN_SKILL_BADGE[skill]) return KNOWN_SKILL_BADGE[skill];
    let hash = 0;
    for (let i = 0; i < skill.length; i++) {
        hash = skill.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % BADGE_COLOR_PALETTES.length;
    return {
        label: formatSkillLabel(skill),
        cls: BADGE_COLOR_PALETTES[idx],
    };
}

const emptyForm: OperatorForm = {
    name: '', employeeCode: '', skill: 'MACHINIST', shiftId: '', wageRatePerHour: '', phone: '', isActive: true,
    skillLevel: 'SKILLED', userId: '',
};

// ─── Wage Rate Formula ────────────────────────────────────────────────────────
// Hourly wage = Monthly CTC (salary + PF + ESI + bonus share) ÷ working hours in the month
function WageFormulaInfo({ onApply }: { onApply: (ratePerHour: number) => void }) {
    const [open, setOpen] = useState(false);
    const [ctc, setCtc] = useState('20000');
    const [days, setDays] = useState('26');
    const [hours, setHours] = useState('8');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const monthlyHours = Number(days) * Number(hours);
    const rate = Number(ctc) > 0 && monthlyHours > 0 ? Math.round(Number(ctc) / monthlyHours) : 0;
    const inputCls = 'w-full h-8 px-2 rounded border border-slate-300 bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]';

    return (
        <div ref={ref} className="relative inline-flex">
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                title="How is the hourly wage calculated?"
                aria-label="Wage calculation formula"
                className={`w-4 h-4 flex items-center justify-center rounded-full border text-[10px] transition-colors cursor-pointer ${open ? 'bg-[#4f46e5] border-[#4f46e5] text-white' : 'border-indigo-300 text-[#4f46e5] bg-indigo-50 hover:bg-indigo-100'}`}
            >
                <i className="ri-information-line" />
            </button>
            {open && (
                <div className="absolute right-0 top-6 z-20 w-80 bg-white rounded-xl border border-slate-200 shadow-xl p-3.5 space-y-3 normal-case tracking-normal animate-in fade-in duration-150">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-[#1e293b]">Wage Rate Calculation</p>
                        <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                            <i className="ri-close-line text-sm" />
                        </button>
                    </div>
                    <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
                        <p className="text-[11px] font-semibold text-indigo-800 leading-relaxed">
                            Wage Rate (₹/hr) = Monthly CTC ÷ Working hours in the month
                        </p>
                        <p className="text-[10px] text-indigo-600 mt-0.5">
                            Monthly CTC = Salary + PF + ESI + bonus share
                        </p>
                    </div>
                    <div className="text-[11px] text-[#64748b] space-y-0.5">
                        <p className="font-semibold text-[#1e293b]">Example</p>
                        <p className="font-mono">₹20,000 ÷ (26 days × 8 hr)</p>
                        <p className="font-mono">= ₹20,000 ÷ 208 = <span className="font-bold text-[#1e293b]">₹96 / hr</span></p>
                    </div>
                    <div className="border-t border-slate-100 pt-2.5 space-y-2">
                        <p className="text-[11px] font-semibold text-[#1e293b]">Calculate</p>
                        <div className="grid grid-cols-3 gap-2">
                            <label className="space-y-0.5">
                                <span className="text-[10px] text-[#64748b]">Monthly CTC ₹</span>
                                <input type="number" min="0" value={ctc} onChange={(e) => setCtc(e.target.value)} className={inputCls} />
                            </label>
                            <label className="space-y-0.5">
                                <span className="text-[10px] text-[#64748b]">Days / month</span>
                                <input type="number" min="0" value={days} onChange={(e) => setDays(e.target.value)} className={inputCls} />
                            </label>
                            <label className="space-y-0.5">
                                <span className="text-[10px] text-[#64748b]">Hours / day</span>
                                <input type="number" min="0" value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} />
                            </label>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] text-[#64748b] font-mono">
                                {monthlyHours > 0 ? `₹${Number(ctc || 0).toLocaleString('en-IN')} ÷ ${monthlyHours} = ` : ''}
                                <span className="font-bold text-[#1e293b]">₹{rate} / hr</span>
                            </p>
                            <button
                                type="button"
                                disabled={rate <= 0}
                                onClick={() => { onApply(rate); setOpen(false); }}
                                className="h-7 px-2.5 rounded bg-[#4f46e5] text-white text-[11px] font-semibold hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                            >
                                Use ₹{rate}/hr
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Slide-Over Form ──────────────────────────────────────────────────────────
interface SlideOverProps {
    open: boolean;
    editing: Operator | null;
    allOperators: Operator[];
    onClose: () => void;
    onSave: (form: OperatorForm) => Promise<void>;
    shifts: ShiftOption[];
    onRefreshShifts: () => void;
    skillOptions: { value: string; label: string }[];
    onAddNewSkill: (name: string) => string;
}

function OperatorSlideOver({ open, editing, allOperators, onClose, onSave, shifts, onRefreshShifts, skillOptions, onAddNewSkill }: SlideOverProps) {
    // Login users, to link the operator's own login ("My Work Orders", assignment notifications)
    const [users, setUsers] = useState<{ id: string; name: string; email?: string; role?: string }[]>([]);
    useEffect(() => {
        if (!open) return;
        userService.list({ limit: 500 })
            .then((res) => setUsers((res?.items ?? []) as any[]))
            .catch(() => setUsers([]));
    }, [open]);
    const userOptions: SearchableOption[] = useMemo(() => [
        { value: '', label: '— No login —' },
        ...users.map((u) => {
            const linkedTo = allOperators.find((o) => o.userId === u.id && o.id !== editing?.id);
            return {
                value: u.id,
                label: u.name,
                sublabel: [u.email, u.role, linkedTo ? `linked to ${linkedTo.name}` : null].filter(Boolean).join(' · '),
            };
        }),
    ], [users, allOperators, editing]);

    const [form, setForm] = useState<OperatorForm>({ ...emptyForm });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingSkill, setIsAddingSkill] = useState(false);
    const [newSkillInput, setNewSkillInput] = useState('');
    const firstInputRef = useRef<HTMLInputElement>(null);
    const createShiftHref = useHref('/masters/shifts?new=1');

    // A shift created in another tab shows up when the user comes back to this form
    useEffect(() => {
        if (!open) return;
        window.addEventListener('focus', onRefreshShifts);
        return () => window.removeEventListener('focus', onRefreshShifts);
    }, [open, onRefreshShifts]);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                name: editing?.name || '',
                employeeCode: editing?.employeeCode || '',
                skill: editing?.skill || 'MACHINIST',
                shiftId: editing?.shiftId || '',
                wageRatePerHour: editing?.wageRatePerHour?.toString() || '',
                phone: editing?.phone ?? '',
                isActive: editing?.isActive ?? true,
                skillLevel: editing?.skillLevel || 'SKILLED',
                userId: editing?.userId || '',
            });
        } else {
            setForm({ ...emptyForm });
            // Suggest the next EMP-xxx code; the user may overwrite it
            getNextOperatorCode()
                .then((res) => {
                    const code = res.data?.employee_code;
                    if (code) setForm((p) => (p.employeeCode ? p : { ...p, employeeCode: code }));
                })
                .catch(() => { /* server assigns a code on save */ });
        }
        setErrors({});
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [open, editing]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const update = (field: keyof OperatorForm, value: string | boolean) => {
        setForm((p) => ({ ...p, [field]: value }));
        if (errors[field]) setErrors((e) => { const n = { ...e }; delete n[field]; return n; });
    };

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.name.trim()) e.name = 'Full name is required';
        if (!form.employeeCode.trim()) e.employeeCode = 'Employee code is required';
        else {
            const dup = allOperators.find(
                (op) => op.employeeCode.toLowerCase() === form.employeeCode.trim().toLowerCase() && op.id !== editing?.id,
            );
            if (dup) e.employeeCode = `Code "${form.employeeCode}" already used by ${dup.name}`;
        }
        if (!form.skill) e.skill = 'Primary skill is required';
        if (!form.skillLevel) e.skillLevel = 'Skill level is required';
        if (!form.wageRatePerHour.trim() || Number(form.wageRatePerHour) <= 0) e.wageRatePerHour = 'Wage rate is required';
        if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) e.phone = 'Phone must be a 10-digit number';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(form);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const availableShifts = shifts.filter((s) => s.isActive || s.id === form.shiftId);

    const shiftSelectOptions: SearchableOption[] = useMemo(() => [
        { value: '', label: '— No shift assigned (optional) —' },
        ...availableShifts.map((s) => ({
            value: s.id,
            label: s.name,
            sublabel: `${s.startTime} – ${s.endTime}`,
        })),
    ], [availableShifts]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[600] flex justify-end">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-2xs transition-opacity" onClick={onClose} />
            <div className="relative bg-white w-full max-w-xl flex flex-col h-full shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#4f46e5] text-lg shrink-0 shadow-2xs">
                            <i className="ri-user-settings-line" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-[#1e293b]">
                                {editing ? 'Edit Operator' : 'Add Operator'}
                            </h3>
                            <p className="text-xs text-[#64748b] mt-0.5">
                                {editing ? 'Update operator details, skills and shift' : 'Register a new operator in master database'}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] hover:text-[#1e293b] transition-colors cursor-pointer"
                        title="Close"
                    >
                        <i className="ri-close-line text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 pb-36 space-y-5 bg-slate-50/30">
                    {/* Section 1: Profile & Identity */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-user-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Profile & Identification
                            </h4>
                        </div>

                        {/* Code + Name */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                            <div className="space-y-1.5 sm:col-span-1">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                        Employee Code <span className="text-red-500">*</span>
                                    </label>
                                    <span className="text-[10px] text-slate-400 font-medium">Auto</span>
                                </div>
                                <input
                                    type="text"
                                    value={form.employeeCode}
                                    onChange={(e) => update('employeeCode', e.target.value.toUpperCase())}
                                    placeholder="EMP-001"
                                    maxLength={50}
                                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white font-mono uppercase tracking-wider focus:outline-none focus:ring-2 transition-colors ${errors.employeeCode ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                />
                                {errors.employeeCode ? (
                                    <p className="text-xs text-red-500">{errors.employeeCode}</p>
                                ) : (
                                    <p className="text-[10px] text-[#94a3b8]">Auto-generated, editable</p>
                                )}
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    ref={firstInputRef}
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => update('name', e.target.value)}
                                    placeholder="e.g. Ramesh Yadav"
                                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.name ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                />
                                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                            </div>
                        </div>

                        {/* Phone + Wage Rate */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Phone Number</label>
                                    <span className="text-[10px] text-slate-400">10-digit</span>
                                </div>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={form.phone}
                                    onChange={(e) => update('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="e.g. 9876543210"
                                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.phone ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                />
                                {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                        Wage Rate (₹/hr) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-slate-400">Hourly rate</span>
                                        <WageFormulaInfo onApply={(rate) => update('wageRatePerHour', String(rate))} />
                                    </div>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.wageRatePerHour}
                                    onChange={(e) => update('wageRatePerHour', e.target.value)}
                                    placeholder="e.g. 180"
                                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.wageRatePerHour ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                />
                                {errors.wageRatePerHour && <p className="text-xs text-red-500">{errors.wageRatePerHour}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Skill & Shift Assignment */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-award-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Skill & Shift Assignment
                            </h4>
                        </div>


                        {/* Login user */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Login User</label>
                            <SearchableSelect
                                value={form.userId}
                                onChange={(v) => update('userId', v)}
                                options={userOptions}
                                placeholder="— No login —"
                                searchPlaceholder="Search user..."
                            />
                            <p className="text-[11px] text-[#94a3b8]">The operator sees their assigned work in My Work Orders and gets a notification when a work order is assigned.</p>
                        </div>
                        {/* Primary Skill */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                    Primary Skill <span className="text-red-500">*</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setIsAddingSkill((p) => !p)}
                                    title="Add new primary skill"
                                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-[#4f46e5] text-xs font-semibold transition-colors cursor-pointer"
                                >
                                    <i className="ri-add-line text-sm font-bold" />
                                    <span>Add Skill</span>
                                </button>
                            </div>

                            {isAddingSkill && (
                                <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50/50 border border-indigo-200 animate-in fade-in duration-150">
                                    <input
                                        type="text"
                                        value={newSkillInput}
                                        onChange={(e) => setNewSkillInput(e.target.value)}
                                        placeholder="Enter new skill (e.g. Electrician)..."
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (newSkillInput.trim()) {
                                                    const val = onAddNewSkill(newSkillInput.trim());
                                                    update('skill', val);
                                                    setNewSkillInput('');
                                                    setIsAddingSkill(false);
                                                }
                                            } else if (e.key === 'Escape') {
                                                setIsAddingSkill(false);
                                                setNewSkillInput('');
                                            }
                                        }}
                                        className="flex-1 h-8 px-2.5 rounded border border-slate-300 bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (newSkillInput.trim()) {
                                                const val = onAddNewSkill(newSkillInput.trim());
                                                update('skill', val);
                                                setNewSkillInput('');
                                                setIsAddingSkill(false);
                                            }
                                        }}
                                        className="h-8 px-3 rounded bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
                                    >
                                        Add
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsAddingSkill(false);
                                            setNewSkillInput('');
                                        }}
                                        className="h-8 px-2 rounded border border-slate-300 text-xs text-[#64748b] hover:bg-white cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-2">
                                {skillOptions.map((s) => {
                                    const badge = getSkillBadge(s.value);
                                    const isSelected = form.skill === s.value;
                                    return (
                                        <button
                                            key={s.value}
                                            type="button"
                                            onClick={() => update('skill', s.value)}
                                            className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                                                isSelected
                                                    ? `${badge.cls} border-current ring-2 ring-current/20 shadow-xs font-bold`
                                                    : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40 hover:text-[#4f46e5] bg-white'
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.skill && <p className="text-xs text-red-500">{errors.skill}</p>}
                        </div>

                        {/* Skill Level */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Skill Level <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {SKILL_LEVEL_OPTIONS.map((lvl) => {
                                    const isSelected = form.skillLevel === lvl.value;
                                    return (
                                        <button
                                            key={lvl.value}
                                            type="button"
                                            onClick={() => update('skillLevel', lvl.value)}
                                            className={`flex flex-col items-start px-3 py-2 rounded-lg border text-left transition-all cursor-pointer ${
                                                isSelected
                                                    ? `${lvl.cls} border-current ring-2 ring-current/20 shadow-xs`
                                                    : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40 hover:text-[#4f46e5] bg-white'
                                            }`}
                                        >
                                            <span className="text-xs font-semibold">{lvl.label}</span>
                                            <span className="text-[10px] opacity-80 leading-tight">{lvl.hint}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.skillLevel && <p className="text-xs text-red-500">{errors.skillLevel}</p>}
                        </div>

                        {/* Assigned Shift */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                    Assigned Shift
                                </label>
                                <a
                                    href={createShiftHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Opens Shifts in a new tab; this list refreshes when you come back"
                                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-[#4f46e5] text-xs font-semibold transition-colors cursor-pointer"
                                >
                                    <i className="ri-add-line text-sm font-bold" />
                                    <span>Create Shift</span>
                                </a>
                            </div>
                            <SearchableSelect
                                value={form.shiftId}
                                onChange={(val) => update('shiftId', val)}
                                options={shiftSelectOptions}
                                placeholder={availableShifts.length === 0 ? '— No shifts yet (optional) —' : '— No shift / Select Shift —'}
                                searchPlaceholder="Search shifts..."
                            />
                            {availableShifts.length === 0 && (
                                <p className="text-[11px] text-[#94a3b8]">No shifts in this warehouse. You can save without a shift and assign it later.</p>
                            )}
                            {errors.shiftId && <p className="text-xs text-red-500">{errors.shiftId}</p>}
                        </div>
                    </div>

                    {/* Section 3: Active Toggle */}
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs">
                        <MasterToggle
                            checked={form.isActive}
                            onChange={(val) => update('isActive', val)}
                            activeLabel="Active — operator is available for work order assignments"
                            inactiveLabel="Inactive — operator is disabled and hidden from new assignments"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-white shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 h-9 px-5 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                    >
                        {isSaving ? (
                            <>
                                <i className="ri-loader-4-line animate-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line" /> {editing ? 'Update Operator' : 'Save Operator'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OperatorsPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const [operators, setOperators] = useState<Operator[]>([]);
    const [shifts, setShifts] = useState<ShiftOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [skillFilter, setSkillFilter] = useState<OperatorSkill | 'ALL'>('ALL');
    const [shiftFilter, setShiftFilter] = useState<string>('ALL');
    const [slideOver, setSlideOver] = useState<{ open: boolean; editing: Operator | null }>({ open: false, editing: null });
    const [deleteConfirm, setDeleteConfirm] = useState<Operator | null>(null);

    const { selectedWarehouseId, selectedWarehouse } = useWarehouseStore();
    const selectedWarehouseName = selectedWarehouse?.name;

    const parseTime = (t: string): string => {
        if (!t) return '';
        const parts = t.split(':');
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
        }
        return t;
    };

    const mapApiToOperator = (o: OperatorResponse): Operator => ({
        id: o.id,
        name: o.name,
        employeeCode: o.employee_code,
        skill: o.skill,
        wageRatePerHour: Number(o.wage_rate_per_hour || 0),
        shiftId: o.shift_id || '',
        shiftName: o.shift_name || '',
        phone: o.phone || '',
        isActive: o.is_active,
        warehouseId: o.warehouse_id || null,
        skillLevel: o.skill_level || 'SKILLED',
        userId: o.user_id || null,
        userName: o.user_name || null,
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [opRes, shRes] = await Promise.all([
                getAllOperators(),
                getAllShifts(selectedWarehouseId || undefined)
            ]);
            if (shRes.success && shRes.data) {
                setShifts(
                    shRes.data.map((s) => ({
                        id: s.id,
                        name: s.name,
                        startTime: parseTime(s.start_time),
                        endTime: parseTime(s.end_time),
                        isActive: s.is_active,
                        warehouseId: s.warehouse_id || null,
                    }))
                );
            }
            if (opRes.success && opRes.data) {
                setOperators(opRes.data.map(mapApiToOperator));
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedWarehouseId]);

    const warehouseOperators = useMemo(() => {
        if (!selectedWarehouseId) return operators;
        return operators.filter((op) => op.warehouseId === selectedWarehouseId);
    }, [operators, selectedWarehouseId]);

    const warehouseShifts = useMemo(() => {
        if (!selectedWarehouseId) return shifts;
        return shifts.filter((s) => s.warehouseId === selectedWarehouseId);
    }, [shifts, selectedWarehouseId]);

    const filtered = useMemo(() => {
        return warehouseOperators.filter((op) => {
            const q = search.toLowerCase();
            const matchSearch = !q || op.name.toLowerCase().includes(q) || op.employeeCode.toLowerCase().includes(q);
            const matchSkill = skillFilter === 'ALL' || op.skill === skillFilter;
            const matchShift = shiftFilter === 'ALL' || op.shiftId === shiftFilter;
            return matchSearch && matchSkill && matchShift;
        });
    }, [warehouseOperators, search, skillFilter, shiftFilter]);

    const refreshShifts = useCallback(async () => {
        try {
            const shRes = await getAllShifts(selectedWarehouseId || undefined);
            if (shRes.success && shRes.data) {
                setShifts(
                    shRes.data.map((s) => ({
                        id: s.id,
                        name: s.name,
                        startTime: parseTime(s.start_time),
                        endTime: parseTime(s.end_time),
                        isActive: s.is_active,
                        warehouseId: s.warehouse_id || null,
                    }))
                );
            }
        } catch (e) {
            console.error('Failed to refresh shifts', e);
        }
    }, [selectedWarehouseId]);

    const openAdd = () => {
        void refreshShifts();
        setSlideOver({ open: true, editing: null });
    };
    const openEdit = (op: Operator) => {
        void refreshShifts();
        setSlideOver({ open: true, editing: op });
    };
    const closeSlideOver = () => setSlideOver({ open: false, editing: null });

    const handleSave = async (form: OperatorForm) => {
        try {
            if (slideOver.editing) {
                const res = await updateOperator(slideOver.editing.id, {
                    name: form.name,
                    employeeCode: form.employeeCode,
                    skill: form.skill,
                    shiftId: form.shiftId || null,
                    wageRatePerHour: Number(form.wageRatePerHour),
                    phone: form.phone || null,
                    isActive: form.isActive,
                    skillLevel: form.skillLevel,
                    userId: form.userId || null,
                });
                if (res.success && res.data) {
                    void fetchData();
                    setOperators((prev) =>
                        prev.map((op) =>
                            op.id === slideOver.editing!.id
                                ? mapApiToOperator(res.data!)
                                : op,
                        ),
                    );
                    toast.success('Operator updated successfully');
                }
            } else {
                const res = await createOperator({
                    name: form.name,
                    employeeCode: form.employeeCode,
                    skill: form.skill,
                    shiftId: form.shiftId || null,
                    wageRatePerHour: Number(form.wageRatePerHour),
                    phone: form.phone || null,
                    isActive: form.isActive,
                    warehouseId: selectedWarehouseId || null,
                    skillLevel: form.skillLevel,
                    userId: form.userId || null,
                });
                if (res.success && res.data) {
                    setOperators((prev) => [mapApiToOperator(res.data!), ...prev]);
                    toast.success('Operator registered successfully');
                }
            }
            closeSlideOver();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to save operator');
            throw err;
        }
    };

    const handleToggleActive = async (op: Operator) => {
        // Optimistic update
        setOperators((prev) => prev.map((x) => x.id === op.id ? { ...x, isActive: !x.isActive } : x));
        try {
            await updateOperator(op.id, { isActive: !op.isActive });
            toast.success(`${op.name} ${op.isActive ? 'deactivated' : 'activated'}`);
        } catch (err) {
            // Rollback
            setOperators((prev) => prev.map((x) => x.id === op.id ? { ...x, isActive: op.isActive } : x));
            toast.error(err instanceof Error ? err.message : 'Failed to update status');
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await deleteOperator(deleteConfirm.id);
            setOperators((prev) => prev.filter((op) => op.id !== deleteConfirm.id));
            toast.success(`"${deleteConfirm.name}" removed`);
            setDeleteConfirm(null);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete operator');
        }
    };

    const getShiftLabel = (shiftId: string | null, shiftName?: string | null): string => {
        if (!shiftId) return 'No shift assigned';
        const sh = warehouseShifts.find((s) => s.id === shiftId) || shifts.find((s) => s.id === shiftId);
        if (sh) return `${sh.name} (${sh.startTime}-${sh.endTime})`;
        if (shiftName) return shiftName;
        return 'No shift assigned';
    };

    const [customSkills, setCustomSkills] = useState<{ value: string; label: string }[]>(() => {
        try {
            const saved = localStorage.getItem('operator_custom_skills');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    const allSkillOptions = useMemo(() => {
        const list = [...BASE_SKILL_OPTIONS];
        const seen = new Set(list.map((s) => s.value));

        for (const cs of customSkills) {
            if (!seen.has(cs.value)) {
                list.push(cs);
                seen.add(cs.value);
            }
        }

        for (const op of operators) {
            if (op.skill && !seen.has(op.skill)) {
                list.push({ value: op.skill, label: formatSkillLabel(op.skill) });
                seen.add(op.skill);
            }
        }
        return list;
    }, [customSkills, operators]);

    const handleAddNewSkill = (name: string): string => {
        const formattedVal = name.trim().toUpperCase().replace(/\s+/g, '_');
        const label = name.trim();
        const newSkill = { value: formattedVal, label };
        setCustomSkills((prev) => {
            if (prev.some((s) => s.value === formattedVal)) return prev;
            const updated = [...prev, newSkill];
            try {
                localStorage.setItem('operator_custom_skills', JSON.stringify(updated));
            } catch {}
            return updated;
        });
        toast.success(`Skill "${label}" added`);
        return formattedVal;
    };

    // Skill counts
    const skillCounts = useMemo(() => {
        return allSkillOptions.map((s) => ({
            ...s,
            count: warehouseOperators.filter((op) => op.skill === s.value).length,
            badge: getSkillBadge(s.value),
        }));
    }, [allSkillOptions, warehouseOperators]);

    const skillFilterOptions: SearchableOption[] = useMemo(() => [
        { value: 'ALL', label: 'All Skills' },
        ...allSkillOptions.map((s) => ({ value: s.value, label: s.label })),
    ], [allSkillOptions]);

    const shiftFilterOptions: SearchableOption[] = useMemo(() => [
        { value: 'ALL', label: 'All Shifts' },
        ...warehouseShifts.filter((s) => s.isActive).map((s) => ({
            value: s.id,
            label: s.name,
            sublabel: `${s.startTime} – ${s.endTime}`,
        })),
    ], [warehouseShifts]);

    const hasActiveFilters = Boolean(search || skillFilter !== 'ALL' || shiftFilter !== 'ALL');

    return (
        <AppLayout>
            <div className="p-6 space-y-5 w-full max-w-[1600px] mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer shadow-2xs"
                            title="Go back"
                        >
                            <i className="ri-arrow-left-line text-base" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-[#1e293b]">Operators</h1>
                                {selectedWarehouseName && (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                        <i className="ri-store-2-line text-xs" />
                                        {selectedWarehouseName}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-[#64748b] mt-0.5">Manage operators, skills, wages and shift assignments</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch('');
                                    setSkillFilter('ALL');
                                    setShiftFilter('ALL');
                                }}
                                className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-1.5"
                            >
                                <i className="ri-filter-off-line text-sm" />
                                Clear Filters
                            </button>
                        )}
                        <button
                            onClick={openAdd}
                            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                        >
                            <i className="ri-add-line text-base" /> Add Operator
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px] max-w-sm">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or employee code..."
                            className="w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] shadow-2xs"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <i className="ri-close-line text-sm" />
                            </button>
                        )}
                    </div>

                    <div className="w-52">
                        <SearchableSelect
                            value={skillFilter}
                            onChange={(val) => setSkillFilter(val as any)}
                            options={skillFilterOptions}
                            placeholder="All Skills"
                            searchPlaceholder="Search skills..."
                        />
                    </div>

                    <div className="w-60">
                        <SearchableSelect
                            value={shiftFilter}
                            onChange={(val) => setShiftFilter(val)}
                            options={shiftFilterOptions}
                            placeholder="All Shifts"
                            searchPlaceholder="Search shifts..."
                        />
                    </div>
                </div>

                {/* Summary cards */}
                <MasterSummaryCards
                    items={skillCounts.map((s) => ({
                        value: s.value,
                        count: s.count,
                        label: s.badge.label,
                        badgeClass: s.badge.cls,
                    }))}
                    activeFilterValue={skillFilter}
                    onFilterChange={(val) => setSkillFilter(val)}
                />

                {/* Stats row */}
                <MasterStatsRow
                    stats={[
                        { label: 'Total Operators', value: warehouseOperators.length, icon: 'ri-user-settings-line', bg: 'bg-indigo-50', color: 'text-[#4f46e5]' },
                        { label: 'Active', value: warehouseOperators.filter((op) => op.isActive).length, icon: 'ri-checkbox-circle-line', bg: 'bg-green-50', color: 'text-green-600' },
                        { label: 'Avg Wage/hr', value: `₹${Math.round(warehouseOperators.reduce((a, op) => a + op.wageRatePerHour, 0) / (warehouseOperators.length || 1))}`, icon: 'ri-money-rupee-circle-line', bg: 'bg-amber-50', color: 'text-amber-600' },
                    ]}
                />

                {/* Table */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-260px)]">
                        <table className="w-full min-w-[800px] text-sm border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                    {['Employee Code', 'Name', 'Skill', 'Shift', 'Wage Rate/hr', 'Status', ''].map((h) => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-12 text-center">
                                            <i className="ri-loader-4-line text-4xl text-[#4f46e5] animate-spin block mb-2" />
                                            <p className="text-[#94a3b8] text-sm">Loading operators...</p>
                                        </td>
                                    </tr>
                                ) : filtered.map((op, idx) => {
                                    const badge = getSkillBadge(op.skill);
                                    const level = skillLevelBadge(op.skillLevel);
                                    return (
                                        <tr key={op.id} className={`border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-colors ${idx % 2 !== 0 ? 'bg-[#f8fafc]/40' : ''}`}>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-[#4f46e5] bg-indigo-50 px-2 py-1 rounded border border-indigo-100 font-bold">
                                                    <i className="ri-barcode-line text-[10px]" />
                                                    {op.employeeCode}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-indigo-50 text-[#4f46e5] text-xs font-bold border border-indigo-100">
                                                        {op.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-[#1e293b] whitespace-nowrap">{op.name}</p>
                                                        {op.phone && <p className="text-xs text-[#94a3b8]">{op.phone}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${badge.cls}`}>
                                                        {badge.label}
                                                    </span>
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${level.cls}`}>
                                                        {level.label}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-[#1e293b] whitespace-nowrap">
                                                {getShiftLabel(op.shiftId, op.shiftName)}
                                            </td>
                                            <td className="px-4 py-3 font-semibold text-[#1e293b] whitespace-nowrap">
                                                ₹{op.wageRatePerHour}<span className="text-xs font-normal text-[#94a3b8]">/hr</span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button onClick={() => handleToggleActive(op)} className="cursor-pointer" title="Click to toggle status">
                                                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${op.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${op.isActive ? 'bg-green-500' : 'bg-slate-400'}`} />
                                                        {op.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => openEdit(op)}
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#64748b] hover:text-[#4f46e5] transition-colors cursor-pointer" title="Edit">
                                                        <i className="ri-edit-line text-sm" />
                                                    </button>
                                                    <button onClick={() => setDeleteConfirm(op)}
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer" title="Delete">
                                                        <i className="ri-delete-bin-line text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!isLoading && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-12 text-center">
                                            <i className="ri-user-settings-line text-4xl text-[#e2e8f0] block mb-2" />
                                            <p className="text-[#94a3b8] text-sm">No operators found</p>
                                            {(search || skillFilter !== 'ALL' || shiftFilter !== 'ALL') && (
                                                <p className="text-xs text-[#94a3b8] mt-1">Try adjusting your filters</p>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-gray-400 text-right mt-1 px-4 sm:hidden">
                        ← Scroll to see more →
                    </p>
                </div>
            </div>

            <OperatorSlideOver
                open={slideOver.open}
                editing={slideOver.editing}
                allOperators={operators}
                onClose={closeSlideOver}
                onSave={handleSave}
                shifts={warehouseShifts}
                onRefreshShifts={refreshShifts}
                skillOptions={allSkillOptions}
                onAddNewSkill={handleAddNewSkill}
            />

            <ConfirmDialog
                open={!!deleteConfirm}
                title="Remove Operator"
                message={`Remove "${deleteConfirm?.name}" (${deleteConfirm?.employeeCode})? This action cannot be undone.`}
                variant="danger"
                confirmLabel="Yes, Remove"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteConfirm(null)}
            />
        </AppLayout>
    );
}